import { toast } from "sonner";
import {
  AssignClassSubjectPayload,
  AssignedTeacher,
  ClassApiResponse,
  ClassListResponse,
  ClassSubject,
} from "../types/classesTypes";
import { ApiResponse } from "../types/responseType";
import customFetch from "../util/config/axiosConfig";
import { getErrorMessage } from "../util/functions/UniversalFunctions";

const classesApis = {
  // GET all
  fetchClass: async (
    params: Record<string, any>,
  ): Promise<ApiResponse<ClassListResponse> | undefined> => {
    try {
      const response = await customFetch.get<ApiResponse<ClassListResponse>>(
        `/api/v1/class`,
        {
          params,
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching class list:", error);
    }
  },

  // POST
  createClass: async (classData: {
    className: string;
    academicCycleId: number;
    branchCode: string;
  }): Promise<ApiResponse<null> | undefined> => {
    try {
      const response = await customFetch.post<ApiResponse<null>>(
        `/api/v1/class`,
        classData,
      );

      return response.data;
    } catch (error) {
      console.error("Error creating class:", error);
    }
  },

  // DELETE
  deleteClass: async (
    classId: number,
  ): Promise<ApiResponse<null> | undefined> => {
    try {
      const response = await customFetch.delete<ApiResponse<null>>(
        `/api/v1/class/${classId}`,
      );

      return response.data;
    } catch (error) {
      toast.error(getErrorMessage(error));
      console.error(`Error deleting class with ID ${classId}:`, error);
      throw error;
    }
  },

  // GET BY ID
  fetchClassById: async (
    classId: number,
  ): Promise<ApiResponse<ClassApiResponse> | undefined> => {
    try {
      const response = await customFetch.get<ApiResponse<ClassApiResponse>>(
        `/api/v1/class/${classId}`,
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching class with ID ${classId}:`, error);
    }
  },

  // PUT Update class
  updateClass: async (
    classData: {
      className: string;
      academicCycleId: number;
      branchCode: string;
    },
    classId: number,
  ): Promise<ApiResponse<null> | undefined> => {
    try {
      const response = await customFetch.put<ApiResponse<null>>(
        `/api/v1/class/${classId}`,
        classData,
      );

      return response.data;
    } catch (error) {
      console.error("Error updating class:", error);
    }
  },

  // POST Assign subject to class
  assignSubjectToClass: async (
    classId: number,
    payload: AssignClassSubjectPayload,
  ): Promise<ApiResponse<AssignClassSubjectPayload> | undefined> => {
    try {
      const response = await customFetch.post<
        ApiResponse<AssignClassSubjectPayload>
      >(`/api/v1/class/subject/${classId}`, payload);
      toast.success("Subject assigned to class");
      return response.data;
    } catch (error) {
      toast.error(error?.response?.data || "failed to assign subject");
      console.error(`Error assigning subject to class ${classId}:`, error);
    }
  },

  // Get subjects for class
  fetchClassSubjects: async (
    classId: number,
  ): Promise<ApiResponse<ClassSubject[]> | undefined> => {
    try {
      const response = await customFetch.get<ApiResponse<ClassSubject[]>>(
        `/api/v1/class/subject/${classId}`,
      );

      return response.data;
    } catch (error) {
      toast.error("Failed to fetch class subjects");
      console.error(error);
    }
  },

  deleteAssignSubjects: async (
    subjectId: number,
  ): Promise<ApiResponse<null> | undefined> => {
    try {
      const response = await customFetch.delete<ApiResponse<null>>(
        `/api/v1/class/subject/${subjectId}`,
      );
      toast.success("Subject removed from class successfully");
      return response.data;
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to remove subject from class",
      );
      console.error(`Error deleting class with ID ${subjectId}:`, error);
    }
  },

  // GET all assigned teachers for a class
  fetchAssignedTeachers: async (
    classId: number,
  ): Promise<ApiResponse<AssignedTeacher[]> | undefined> => {
    try {
      const response = await customFetch.get<ApiResponse<AssignedTeacher[]>>(
        `/api/v1/class/teacher/all-assigned/${classId}`,
      );

      return response.data;
    } catch (error) {
      toast.error("Failed to fetch assigned teachers");
      console.error("Error fetching assigned teachers:", error);
    }
  },

  // PATCH Assign teacher to class
  assignTeacherToClass: async (
    classId: number,
    staffId: number,
  ): Promise<ApiResponse<string> | undefined> => {
    try {
      const response = await customFetch.patch<ApiResponse<string>>(
        `/api/v1/class/teacher/assign/${classId}`,
        staffId,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      // toast.success("Teacher assigned successfully");
      return response.data;
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to assign teacher");
      console.error("Error assigning teacher:", error);
    }
  },

  // Make a teacher as class teacher
  makeClassTeacher: async (
    classId: number,
    staffId: number,
  ): Promise<ApiResponse<string> | undefined> => {
    try {
      const response = await customFetch.patch<ApiResponse<string>>(
        `/api/v1/class/teacher/make-class-teacher/${classId}`,
        staffId,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      toast.success("Class teacher assigned");
      return response.data;
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to assign class teacher",
      );
      console.error(error);
    }
  },

  // DELETE Un-assign teacher from class
  unAssignTeacher: async (
    classId: number,
    staffId: number,
  ): Promise<ApiResponse<null> | undefined> => {
    try {
      const response = await customFetch.delete<ApiResponse<null>>(
        `/api/v1/class/teacher/un-assign/${classId}`,
        {
          params: { staffId },
        },
      );

      toast.success("Teacher removed successfully");
      return response.data;
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to remove teacher");
      console.error("Error un-assigning teacher:", error);
    }
  },

  // PATCH Move student to class
  moveStudentToClass: async (
    classId: number,
    studentId: number,
  ): Promise<ApiResponse<string> | undefined> => {
    try {
      const response = await customFetch.patch<ApiResponse<string>>(
        `/api/v1/class/student/add/${classId}`,
        studentId,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      toast.success("Student moved to class successfully");
      return response.data;
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to move student");
      console.error("Error moving student:", error);
    }
  },

  // DELETE Remove student from class
  removeStudentFromClass: async (
    studentId: number,
  ): Promise<ApiResponse<string> | undefined> => {
    try {
      const response = await customFetch.delete<ApiResponse<string>>(
        `/api/v1/class/student/remove/${studentId}`,
      );

      toast.success("Student removed from class");
      return response.data;
    } catch (error: any) {
      toast.error(
        getErrorMessage(error) || "Failed to remove student from class",
      );
      console.error("Error removing student:", error);
    }
  },

  // edit teacher for subject
  updateSubjectStaff: async (
    subjectId: number,
    staffId: number,
  ): Promise<ApiResponse<string> | undefined> => {
    try {
      const response = await customFetch.patch<ApiResponse<string>>(
        `/api/v1/class/subject/${subjectId}`,
        staffId,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      toast.success("Staff updated successfully");
      return response.data;
    } catch (error: any) {
      toast.error(error?.response?.data || "Failed to update staff");
      console.error("Error updating subject staff:", error);
    }
  },
};

export default classesApis;
